import React from 'react';
import { format } from 'date-fns';
import { Users, Edit, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { ActionMenu } from './ui/ActionMenu';
import type { Story, Profile, Band } from '../types/database';
import { StoryLikeButton } from './StoryLikeButton';
import { useTranslation } from 'react-i18next';
import { dateLocales } from '../i18n';

interface StoryPanelProps {
  story: Story & {
    author: Profile;
    band: Band;
    likes_count?: number;
  };
  onClick: (story: Story) => void;
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function StoryPanel({ story, onClick, showActions, onEdit, onDelete }: StoryPanelProps) {
  const { i18n } = useTranslation();
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;
  
  const handleClick = (e: React.MouseEvent) => {
    // Prevent modal opening if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest('.action-menu') || 
      target.closest('button') || 
      target.closest('a') ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'A'
    ) {
      return;
    }
    
    onClick(story);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Prevent double-tap zoom on mobile
    e.preventDefault();
  };

  return (
    <motion.div
      key={story.id}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      className="text-left block w-full relative group cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="rounded-t-xl bg-gray-custom/10 hover:bg-gray-custom/20 transition-colors">
        <motion.div 
          className="p-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {story.band && (
                <motion.div 
                  className="w-12 h-12 bg-gray-custom/10 flex items-center justify-center overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  {story.band.avatar_url ? (
                    <img
                      src={story.band.avatar_url}
                      alt={story.band.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Users className="w-4 h-4 text-light/60" />
                  )}
                </motion.div>
              )}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {story.band && (<h3 className="font-medium text-light">{story.band.name}</h3>)}
                <div className="text-sm text-light/60">
                  {format(new Date(story.created_at), 'PPP', { locale })}
                </div>
              </motion.div>
            </div>
            {showActions && (
              <div className="md:opacity-0 group-hover:opacity-100 transition-opacity action-menu">
                <ActionMenu
                  items={[
                    {
                      label: 'Edit Story',
                      icon: <Edit className="w-4 h-4" />,
                      onClick: onEdit,
                    },
                    {
                      label: 'Delete Story',
                      icon: <Trash2 className="w-4 h-4" />,
                      onClick: onDelete,
                      className: 'text-red-500 hover:text-red-500',
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </motion.div>
  
        <motion.div 
          className="bg-white p-6 rounded-b-xl"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className="font-tilt-warp text-2xl text-dark mb-2">{story.title}</h2>
          <p className="text-dark/60 line-clamp-3 whitespace-pre-line">{story.content}</p>
          <motion.div 
            className="mt-4 pt-4 border-t border-dark/10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <StoryLikeButton 
                storyId={story.id} 
                initialLikes={story.likes_count}
                className="!text-primary"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
}